import { computed, ref, watch } from 'vue';

import { defineStore } from 'pinia';

import { useAccountStore } from '@/stores/account.js';

// A sales channel beyond Amazon (eBay today) scopes its analytics to ONE connected account
// at a time, picked in the page filters. This factory holds that selection: the business
// profile's accounts, the selected id, and its per-BP memory in localStorage. It is
// channel-agnostic on purpose — a later channel (Shopify) supplies its own fetcher and
// storage prefix instead of copying the store.
//
// Accounts are keyed by the integration service's account id, never by the channel's own
// user id or name (those are display-only). Disconnected / revoked accounts stay selectable:
// their historical data is still readable, and the select shows their status.
//
// The selection belongs to one business profile, so a BP switch clears it synchronously —
// the olap interceptor must never send an id of the previous profile (olap answers 403).

const readStored = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const id = raw == null ? null : Number(raw);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

const writeStored = (key, id) => {
  try {
    if (id == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(id));
  } catch {
    // Storage unavailable (private mode / quota) — the selection still works in memory.
  }
};

// The stored id when it is still one of the accounts, else the first ACTIVE one, else the
// first at all (a BP whose only account was disconnected still reads its history).
export const pickInitialAccountId = (accounts, storedId) => {
  if (!accounts.length) return null;
  if (storedId != null && accounts.some((a) => a.id === storedId)) return storedId;
  return (accounts.find((a) => a.status === 'ACTIVE') ?? accounts[0]).id;
};

export const defineChannelAccountStore = (id, { storagePrefix, fetchAccounts }) =>
  defineStore(id, () => {
    const account = useAccountStore();

    const accounts = ref([]);
    const selectedAccountId = ref(null);
    // The business profile `accounts` was loaded for; null until the first load settles.
    const loadedForBpId = ref(null);
    const loading = ref(false);
    const loadError = ref(null);
    let inflight = null;

    const storageKey = (bpId) => `${storagePrefix}.${bpId}`;
    const currentBpId = () => account.currentBusinessProfile?.id ?? null;

    const isLoaded = computed(
      () => loadedForBpId.value != null && loadedForBpId.value === currentBpId(),
    );
    const hasAccounts = computed(() => accounts.value.length > 0);
    const selectedAccount = computed(
      () => accounts.value.find((a) => a.id === selectedAccountId.value) ?? null,
    );

    const reset = () => {
      accounts.value = [];
      selectedAccountId.value = null;
      loadedForBpId.value = null;
      loadError.value = null;
      loading.value = false;
      inflight = null;
    };

    const load = () => {
      const bpId = currentBpId();
      if (!bpId) return Promise.resolve();
      const request = (async () => {
        loading.value = true;
        loadError.value = null;
        try {
          const rows = (await fetchAccounts()) || [];
          // A BP switch (or a newer load) raced this one — its answer belongs to another profile.
          if (inflight !== request || currentBpId() !== bpId) return;
          accounts.value = rows;
          selectedAccountId.value = pickInitialAccountId(rows, readStored(storageKey(bpId)));
          loadedForBpId.value = bpId;
        } catch (error) {
          if (inflight !== request || currentBpId() !== bpId) return;
          loadError.value = error;
          accounts.value = [];
          selectedAccountId.value = null;
          loadedForBpId.value = bpId;
        } finally {
          if (inflight === request) {
            inflight = null;
            loading.value = false;
          }
        }
      })();
      inflight = request;
      return request;
    };

    // Load once per business profile; concurrent callers share the in-flight request.
    const ensureLoaded = () => {
      if (isLoaded.value) return Promise.resolve();
      return inflight ?? load();
    };

    const selectAccount = (accountId) => {
      const bpId = currentBpId();
      if (!bpId || !accounts.value.some((a) => a.id === accountId)) return;
      selectedAccountId.value = accountId;
      writeStored(storageKey(bpId), accountId);
    };

    // flush: 'sync' — cleared in the same tick as the switch, before any request can go out.
    watch(
      currentBpId,
      (bpId, prevBpId) => {
        if (bpId !== prevBpId) reset();
      },
      { flush: 'sync' },
    );

    return {
      accounts,
      selectedAccountId,
      selectedAccount,
      hasAccounts,
      isLoaded,
      loading,
      loadError,
      load,
      ensureLoaded,
      selectAccount,
      reset,
    };
  });
